import React from "react";
import classNames from "classnames";
import ErrorField from "../errorField";
import { useField, useFormikContext } from "formik";
import FormGroup from "shared/form/fields/formGroup";
import FormLabel from "shared/form/fields/formLabel";
import { DateTimePickerFieldConfig } from "../types";
import DateTimePicker from "shared/form/core/dateTimePicker";
import FormOverwriteField from "../overwriteField";
import { isEmpty } from "../../../utils";
import { ConflictType } from "shared/form/renderer/enums/Conflict";
import { useFormContextData } from "shared/form/context";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import { DateRepresentationEnum } from "shared/utils/dateTimeHelper/enums";

/**
 * @name FormDateTimePickerField
 * @type DateTimePickerFieldConfig
 * @param props
 */
function FormDateTimePickerField(props: DateTimePickerFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldValue, setFieldTouched, validateForm, validateOnChange } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  const getDateValue = React.useCallback(
    (value: number | Date) => {
      if (!value) {
        return "";
      }
      return props.transformDate?.(value) || props.formattingOptions?.showOnlyTime
        ? DateTimeHelper.getExactDate(value, DateRepresentationEnum.TIME)
        : DateTimeHelper.getExactDate(value);
    },
    [props]
  );
  const handleChange = React.useCallback(
    async value => {
      let dateValue = getDateValue(value);
      await setFieldValue(props.name, dateValue, false);
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
    },
    [getDateValue, meta.touched, props.name, setFieldValue, setFieldTouched, validateForm, validateOnChange]
  );

  const handleBlur = React.useCallback(
    e => {
      setFieldTouched(props.name, true);
    },
    [props.name, setFieldTouched]
  );

  const selectedDate = React.useMemo(() => {
    let date;
    if (props.formattingOptions?.showOnlyTime) {
      date = field.value && DateTimeHelper.parseDate(DateTimeHelper.getDateWithTime(new Date(), field.value));
    } else {
      date = field.value && DateTimeHelper.parseDate(field.value);
    }
    return DateTimeHelper.isValidDate(date) ? date : null;
  }, [field.value, props.formattingOptions?.showOnlyTime]);

  const formattingOptions = React.useMemo(
    () => DateTimeHelper.getFormattingOptionFormInputField(props.formattingOptions),
    [props.formattingOptions]
  );

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
        <FormLabel
          label={props.label}
          htmlFor={props.name}
          required={props.required}
          additionalLabel={props.additionalLabel}
          infoLabel={props.infoLabel}
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({
            renderType: FieldRenderType.DATE,
            value: field.value
          })
        ) : (
          <div
            className={classNames("dateTimePicker", props.className, {
              hasError: meta.touched && meta.error
            })}>
            <DateTimePicker
              name={props.name}
              onBlur={handleBlur}
              onChange={handleChange}
              autoComplete={props.autoComplete}
              selected={selectedDate}
              className={props.className}
              disabled={props.disabled}
              placeholder={props.placeholder}
              openToDate={props.openToDate}
              minDate={props.minDate}
              maxDate={props.maxDate}
              formattingOptions={formattingOptions}
              timeZoneDisplayStr={props.timeZoneDisplayStr}
              isEndDate={props.isEndDate}
            />
          </div>
        )}

        <ErrorField meta={meta} />
        {!isEmpty(props.conflictValue) && (
          <FormOverwriteField
            forFieldName={props.name}
            disabled={props.disabled}
            conflictType={ConflictType.DATE}
            formattingOptions={formattingOptions}
          />
        )}
        {props.resetFieldWhenRemoved && (
          <FieldResetWhenRemoved name={props.name} fixedInitialValueOnReset={props.fixedInitialValueOnReset} />
        )}
      </FormGroup>
    </React.Fragment>
  );
}

export default FormDateTimePickerField;
